import { useEffect, useState, useCallback } from "react";
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { makeRequestAuth } from "../../_config/api"; // Path check kar lein
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Spinner from "../../utils/Loader";
import ViewSellModal from "./ViewSellModal"; // Ensure ViewSellModal is in the same folder

function UsdtSellList() {
  const [sellOrders, setSellOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination & Filtering States
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);

  const [keyWord, setKeyWord] = useState("");
  const [status, setStatus] = useState("");

  // Modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Approve/Reject Action Modal
  const [actionModal, setActionModal] = useState({
    open: false,
    type: "", // 'approve' | 'reject'
    id: null,
    rejectReason: "",
    amount: "",
  });
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  const fetchSellOrders = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const response = await makeRequestAuth("get", "/admin/getSellList", {
          page,
          limit: pageSize,
          keyWord,
          status,
          walletType: "USDT", // Hardcoded to USDT
        });

        const payload = response?.data || {};
        setSellOrders(payload.data || []);
        setTotal(payload.total || 0);
        setTotalPages(payload.pages || 1);
        setCurrentPage(payload.page || 1);
      } catch (err) {
        showErrorToast(err.message || "Failed to fetch USDT sell orders");
      } finally {
        setIsLoading(false);
      }
    },
    [keyWord, status, pageSize],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchSellOrders(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [keyWord, status, fetchSellOrders]);

  const getStatusStyle = (statusVal) => {
    switch (statusVal) {
      case "approve":
        return "bg-emerald-100 text-emerald-600 border-emerald-200";
      case "reject":
        return "bg-red-100 text-red-600 border-red-200";
      case "pending":
        return "bg-yellow-100 text-yellow-600 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const handleActionSubmit = async () => {
    if (actionModal.type === "reject" && !actionModal.rejectReason.trim()) {
      return showErrorToast("Please provide a reason for rejection.");
    }

    if (actionModal.type === "approve" && !actionModal.amount) {
      return showErrorToast("Please enter payment amount.");
    }

    setIsProcessingAction(true);

    try {
      const response = await makeRequestAuth(
        "post",
        "/admin/updateSellStatus",
        {
          sellId: actionModal.id,
          status: actionModal.type,
          hash: actionModal.type === "approve" ? actionModal.hash : "",
          amount: actionModal.type === "approve" ? actionModal.amount : "",
          rejectReason:
            actionModal.type === "reject" ? actionModal.rejectReason : "",
        },
      );

      if (response.error || response.status === false) {
        showErrorToast(response.message || "Failed to update status");
        return;
      }

      showSuccessToast("Transaction updated successfully");

      setSellOrders((prev) =>
        prev.map((item) =>
          item._id === actionModal.id
            ? {
                ...item,
                status: actionModal.type === "approve" ? "process" : "reject",
              }
            : item,
        ),
      );

      fetchSellOrders(1);
      closeActionModal();
    } catch (error) {
      showErrorToast(error.message || "Failed to process transaction");
    } finally {
      setIsProcessingAction(false);
    }
  };

  const openActionModal = (type, id) => {
    setActionModal({
      open: true,
      id,
      type,
      rejectReason: "",
      amount: "",
    });
  };

  const closeActionModal = () => {
    setActionModal({ open: false, type: "", id: null, rejectReason: "" });
  };

  if (isLoading && sellOrders.length === 0) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6 p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white dark:bg-[#1E2738] text-gray-800 dark:text-white rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              USDT Sell Requests
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total {total} requests found
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-2xl">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by amount or hash..."
              value={keyWord}
              onChange={(e) => setKeyWord(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approve">Approved</option>
            <option value="reject">Rejected</option>
          </select>
        </div>

        {/* TABLE */}
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-6 w-16 text-center">#</th>
                  <th className="py-4 px-6">OrderId</th>
                  <th className="py-4 px-6">User</th>
                  <th className="py-4 px-6 text-right">Amount (USDT)</th>
                  <th className="py-4 px-6 text-right">Total (₹)</th>
                  <th className="py-4 px-6 text-right">Remaining (₹)</th>
                  <th className="py-4 px-6 text-center">Status</th>
                  <th className="py-4 px-6 text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sellOrders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-10 text-gray-500">
                      No USDT Sell Requests Found
                    </td>
                  </tr>
                ) : (
                  sellOrders.map((item, index) => (
                    <tr
                      key={item._id}
                      className="hover:bg-blue-50/50 dark:hover:bg-gray-700/30 transition"
                    >
                      <td className="py-4 px-6 text-center">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="py-4 px-6 font-medium">
                        {item.orderId || "Unknown"}
                      </td>
                      <td className="py-4 px-6 font-medium">
                        {item.userId?.name || "Unknown"}
                        <div className="text-xs text-gray-500 mt-1">
                          {item.userId?.email || "-"}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right font-bold text-gray-900 dark:text-gray-200">
                        {item && item.amount ? item.amount.toFixed(2) : 0}
                        <div className="text-xs text-gray-500 font-normal">
                          @ ₹{item.rate}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right font-bold text-emerald-600 dark:text-emerald-400">
                        ₹
                        {item && item.totalAmount
                          ? item.totalAmount.toFixed(2)
                          : 0}
                      </td>
                      <td className="py-4 px-6 text-right font-bold text-emerald-600 dark:text-emerald-400">
                        ₹
                        {item && item.remainAmount
                          ? item.remainAmount.toFixed(2)
                          : 0}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span
                          className={`text-xs font-bold px-3 py-1.5 rounded-full border uppercase tracking-wider ${getStatusStyle(item.status)}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedTransaction(item);
                              setShowViewModal(true);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-lg transition"
                            title="View Details"
                          >
                            <FiEye size={18} />
                          </button>

                          {["pending", "process"].includes(item.status) && (
                            <button
                              onClick={() =>
                                openActionModal("approve", item._id)
                              }
                              className="p-2 text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-900/30 rounded-lg transition"
                              title="Approve"
                            >
                              <FiCheckCircle size={18} />
                            </button>
                          )}
                          {item.status === "pending" && (
                            <button
                              onClick={() =>
                                openActionModal("reject", item._id)
                              }
                              className="p-2 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30 rounded-lg transition"
                              title="Reject"
                            >
                              <FiXCircle size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center px-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => fetchSellOrders(currentPage - 1)}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition"
              >
                <FiChevronLeft size={20} />
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => fetchSellOrders(currentPage + 1)}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition"
              >
                <FiChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ACTION MODAL */}
      {actionModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white dark:bg-[#1E2738] p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-600 animate-fadeIn">
            <div
              className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${actionModal.type === "approve" ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}
            >
              {actionModal.type === "approve" ? (
                <FiCheckCircle size={32} />
              ) : (
                <FiAlertCircle size={32} />
              )}
            </div>

            <h3 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2 capitalize">
              {actionModal.type} Transaction
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              Are you sure you want to {actionModal.type} this transaction?
            </p>

            {actionModal.type === "reject" && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Rejection <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows="3"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-red-500 outline-none transition"
                  placeholder="Enter rejection reason..."
                  value={actionModal.rejectReason}
                  onChange={(e) =>
                    setActionModal({
                      ...actionModal,
                      rejectReason: e.target.value,
                    })
                  }
                ></textarea>
              </div>
            )}

            {actionModal.type === "approve" && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  UTR <span className="text-red-500">*</span>
                </label>
                <input
                  type="type"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                  placeholder="Enter UTR ..."
                  value={actionModal.hash || ""}
                  onChange={(e) =>
                    setActionModal({
                      ...actionModal,
                      hash: e.target.value,
                    })
                  }
                />
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                  placeholder="Enter payment amount..."
                  value={actionModal.amount || ""}
                  onChange={(e) =>
                    setActionModal({
                      ...actionModal,
                      amount: e.target.value,
                    })
                  }
                />
              </div>
            )}

            <div className="flex justify-center gap-3">
              <button
                onClick={closeActionModal}
                disabled={isProcessingAction}
                className="flex-1 px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleActionSubmit}
                disabled={isProcessingAction}
                className={`flex-1 px-5 py-2.5 rounded-xl text-white font-medium shadow-lg transition disabled:opacity-50 flex justify-center items-center ${
                  actionModal.type === "approve"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isProcessingAction ? (
                  <Spinner className="w-5 h-5" />
                ) : (
                  `Confirm ${actionModal.type}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      <ViewSellModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        transaction={selectedTransaction}
        fetchSellOrders={fetchSellOrders}
      />
    </div>
  );
}

export default UsdtSellList;
